import removeExcludedCards from './product-cards.js';
import toasts from './toasts.js';

/**
 * SearchDrawer class definition.
 */
class SearchDrawer {
  $drawer = $('.search-drawer')

  form = {
    $: undefined,
    $fieldset: undefined,
    $query: undefined,
    timeout: undefined,
    timeoutDelay: 700,
  }

  loader = {
    $: undefined,
  }

  topSuggestions = {
    $: undefined,
  }

  suggestedSearches = {
    $: undefined,
    $ul: undefined,
    collections: {
      $: undefined,
      $ul: undefined
    },
    products: {
      $: undefined,
      $ul: undefined
    },
    pages: {
      $: undefined,
      $ul: undefined
    },
    articles: {
      $: undefined,
      $ul: undefined
    },
    searchRoute: undefined,
  }

  popularProducts = {
    $: undefined,
  }

  popularSuggestions = {
    $: undefined,
    $row: undefined,
  }

  noResults = {
    $: undefined
  }

  lastSuggestedSearchEvent = undefined;

  /**
   * SearchDrawer class constructor.
   */
  constructor() {
    if (this.$drawer.length) {
      /**
       * Cache all needed elements within the drawer.
       */
      this.form.$ = this.$drawer.find('.search-form');
      this.form.$fieldset = this.form.$.children('fieldset');
      this.form.$query = this.form.$fieldset.children('#search-drawer__query');

      this.loader.$ = this.$drawer.find('.search-results__loader');

      this.topSuggestions.$ = this.$drawer.find('.top-suggestions');

      this.suggestedSearches.$ = this.$drawer.find('.suggested-searches');
      this.suggestedSearches.$ul = this.suggestedSearches.$.children('ul');

      this.suggestedSearches.collections.$ = this.suggestedSearches.$ul.children('.suggested-searches__collections');
      this.suggestedSearches.collections.$ul = this.suggestedSearches.collections.$.children('ul');

      this.suggestedSearches.products.$ = this.suggestedSearches.$ul.children('.suggested-searches__products');
      this.suggestedSearches.products.$ul = this.suggestedSearches.products.$.children('ul');

      this.suggestedSearches.pages.$ = this.suggestedSearches.$ul.children('.suggested-searches__pages');
      this.suggestedSearches.pages.$ul = this.suggestedSearches.pages.$.children('ul');

      this.suggestedSearches.articles.$ = this.suggestedSearches.$ul.children('.suggested-searches__articles');
      this.suggestedSearches.articles.$ul = this.suggestedSearches.articles.$.children('ul');

      this.suggestedSearches.searchRoute = this.form.$.attr('action');

      this.popularProducts.$ = this.$drawer.find('.popular-products');

      this.popularSuggestions.$ = this.$drawer.find('.popular-suggestions');
      this.popularSuggestions.$row = this.popularSuggestions.$.children('.row');

      this.noResults.$ = this.$drawer.find('.no-results');

      /**
       * Bind some elements' event handlers.
       */
      this.$drawer.on('shown.bs.offcanvas', this.handleDrawerShown);

      this.$drawer.on('hidden.bs.offcanvas', this.handleDrawerHidden);

      this.form.$query.on('input', this.handleQueryInput);

      this.suggestedSearches.$ul.on('mouseenter', 'a', this.handleSuggestedSearchMouseenter);

      this.suggestedSearches.$ul.on('click', 'a', this.handleSuggestedSearchClick);

      this.popularSuggestions.$.on('click', '.product__card a[href]', this.handleProductCardLinkClick);

      document.addEventListener('CartUpdater.updating', this.handleCartUpdatingEvent);

      document.addEventListener('CartUpdater.updated', this.handleCartUpdatedEvent);
    }
  }

  /**
   * Generic $.ajax() request method. Returns a promise that
   * resolves on a successful request or rejects on failure.
   *
   * @param {object} params Request parameters.
   *
   * @returns {Promise}
   */
  request = (params) => {
    return new Promise((resolve, reject) => {
      $.ajax(
        Object.assign({
            success: resolve,
            error(jqXHR) {
              reject(new Error(jqXHR.responseText));
            },
          },
          params
        )
      );
    });
  }

  /**
   * Handles the drawer' shown event, focusing the form's query
   * input. Also publishes the 'search_initiate' analytics event.
   */
  handleDrawerShown = () => {
    this.form.$query.trigger('focus');

    Shopify.analytics.publish('search_initiate');
  }

  /*
   * Handles the drawer's hidden event, clearing the
   * form's query input, which in turn resets the drawer.
   */
  handleDrawerHidden = () => {
    this.form.$query.val('').trigger('input');
  }

  /**
   * Handles the form's query input event, running the query after
   * a short delay so multiple consecutive inputs are debounced.
   */
  handleQueryInput = () => {
    if (this.form.timeout) clearTimeout(this.form.timeout);
    this.form.timeout = setTimeout(this.runQuery, this.form.timeoutDelay);
  }

  /**
   * Performs the user's query, retrieving predictive search suggestions
   * and displaying them (if any exist) or displaying "no results" content
   * otherwise. If the query is empty or results in an error, the drawer
   * is reset. Also publishes the 'search_results_returned' analytics event
   * containing the user's query and total results.
   */
  runQuery = async () => {
    const query = this.form.$query.val();

    if (!query) {
      this.resetDrawer();
    } else {
      this.form.$fieldset.prop('disabled', true);

      await this.request({
        url: this.form.$.data('search-api'),
        data: {
          q: query,
        },
      }).then(async (response) => {
        const {
          resources: {
            results: {
              collections,
              products,
              pages,
              articles
            }
          }
        } = response;

        if (collections.length || products.length || pages.length || articles.length) {
          await this.displaySuggestedSearches(query, collections, products, pages, articles);
        } else {
          this.displayNoResults(query);

          this.topSuggestions.$.show();
          this.suggestedSearches.$.hide();
        }

        return;
      }).catch((error) => {
        console.error(error);
        this.resetDrawer();
      }).finally(() => {
        this.form.$fieldset.prop('disabled', false);
      })
    }
  }

  /**
   * Displays predictive search suggestions following a successful query.
   *
   * Suggested searches for collections and products are first tested to
   * see if any product cards will be visible after running each one (i.e.
   * after calling this.removeExcludedAndUnavailableCards against them)
   * before the suggestion is displayed. If so, a mouseenter event is
   * automatically triggered for the first suggestion (thereby invoking
   * the this.handleSuggestedSearchMouseenter event handler). If not,
   * "no results" content is displayed instead.
   *
   * Each search suggestion category is then shown or hidden based on
   * whether or not it contains any suggestions. If none do, the default
   * Top Suggestions are displayed instead.
   *
   * @param {string} query       The user's query.
   * @param {Array}  collections Collection search suggestions.
   * @param {Array}  products    Product search suggestions.
   * @param {Array}  pages       Page search suggestions.
   * @param {Array}  articles    Article search suggestions.
   */
  displaySuggestedSearches = async (query, collections, products, pages, articles) => {
    this.topSuggestions.$.hide();
    this.suggestedSearches.$.hide();

    this.loader.$.show();

    const suggestedSearchTypes = {
      collections: {
        type: 'collection',
        suggestions: collections,
      },
      products: {
        type: 'product',
        suggestions: products,
      },
      pages: {
        type: 'page',
        suggestions: pages,
      },
      articles: {
        type: 'article',
        suggestions: articles,
      },
    };

    const queryRegExp = new RegExp(query, 'i');

    const promises = [];

    let totalSuggestions = 0;

    /* eslint-disable no-loop-func, no-await-in-loop */

    for (const [key, {
        type,
        suggestions
      }] of Object.entries(suggestedSearchTypes)) {
      promises.push(new Promise((resolve) => {
        (async () => {
          const suggestedSearchesCategory = this.suggestedSearches[key];

          suggestedSearchesCategory.$ul.empty();

          if (suggestions && suggestions.length) {
            for (const suggestion of suggestions) {
              let href;

              if ('collection' === type) {
                href = this.constructSuggestedSearchHref(suggestion.title);
              } else {
                href = suggestion.url;
              }

              const $a = $(`<a data-${type}="${suggestion.url}" href="${href}">${this.boldQueryInSuggestedSearch(queryRegExp, suggestion.title)}</a>`);

              let appendSuggestion = false;

              if (-1 !== ['collection', 'product'].indexOf(type)) {
                appendSuggestion = await this.testSuggestedSearch($a);
              } else {
                appendSuggestion = true;
              }

              if (appendSuggestion) {
                $('<li></li>').append($a).appendTo(suggestedSearchesCategory.$ul);
                totalSuggestions += 1;
              }
            }
          }

          suggestedSearchesCategory.$[suggestedSearchesCategory.$ul.children().length ? 'show' : 'hide']();

          resolve();
        })();
      }));
    }

    /* eslint-enable no-loop-func, no-await-in-loop */

    await Promise.all(promises).then(() => {
      this.loader.$.hide();

      const $collectionOrProductSearches = this.suggestedSearches.$ul.find('a[data-collection], a[data-product]');

      if ($collectionOrProductSearches.length) {
        $collectionOrProductSearches.eq(0).trigger('mouseenter');
      } else {
        this.displayNoResults(query);
      }

      if (this.suggestedSearches.$ul.find('> li > ul > li').length) {
        this.suggestedSearches.$.show();
        this.topSuggestions.$.hide();

        Shopify.analytics.publish('search_results_returned', {
          search_term: query,
          search_results: totalSuggestions,
        });
      } else {
        this.topSuggestions.$.show();
        this.suggestedSearches.$.hide();
      }

      return;
    }).catch(() => undefined);
  }

  /**
   * Constructs the href value for a search suggestion, which
   * contains the locale-aware search route (via Liquid) plus
   * the suggestion as the query string parameter q.
   *
   * @param {string} suggestion The title (text) of the search suggestion.
   *
   * @returns {string}
   */
  constructSuggestedSearchHref = (suggestion) => {
    return this.suggestedSearches.searchRoute + `?q=${suggestion}`;
  };

  /**
   * Bolds the user's query within a search suggestion.
   *
   * @param {RegExp} queryRegExp The user's query as a case-insensitive regular expression.
   * @param {string} suggestion  The title (text) of the search suggestion.
   *
   * @returns {string}
   */
  boldQueryInSuggestedSearch = (queryRegExp, suggestion) => {
    const matches = suggestion.match(queryRegExp);

    if (matches) {
      return suggestion.replace(matches[0], `<strong>${matches[0]}</strong>`);
    } else {
      return suggestion;
    }
  }

  /**
   * Removes excluded and otherwise unavailable product cards from a
   * given $ancestor. See modules/product-cards.js for more information.
   *
   * @param {jQuery} $ancestor Cards' ancestor object.
   */
  removeExcludedAndUnavailableCards = ($ancestor) => {
    removeExcludedCards($ancestor);
    $ancestor.find('.product__card--unavailable').remove();
  }

  /**
   * Performs a suggested search. The request's 'ajax' view parameter causes
   * the alternate templates collection.ajax.json and search.ajax.json to be
   * used to render the results, which are intentionally configured to not
   * display product cards' add-to-cart forms.
   *
   * @param {jQuery}   $a       The link object representing the search suggestion.
   * @param {Function} callback The callback to be performed following the request.
   *
   * @returns {Promise}
   */
  runSuggestedSearch = async ($a, callback) => {
    const isCollection = $a.is('[data-collection]');

    let params;

    if (isCollection) {
      params = {
        url: $a.data('collection'),
        data: {
          view: 'ajax',
        },
      };
    } else {
      params = {
        url: this.suggestedSearches.searchRoute,
        data: {
          q: $a.text(),
          view: 'ajax',
          type: 'product',
        },
      };
    }

    return this.request(params).then((response) => {
      const $response = $(response);

      let $productCards;

      if (isCollection) {
        $productCards = $response.find('#main .filter__products').children();
      } else {
        $productCards = $response.find('#main .search-results').children();
      }

      'function' === typeof callback && callback($productCards);

      return;
    }).catch((error) => {
      console.error(error);
    });
  }

  /**
   * Tests a suggested search to see if any product
   * cards will be visible after performing it.
   *
   * @param {jQuery} $a The link object representing the search suggestion.
   *
   * @returns {boolean}
   */
  testSuggestedSearch = async ($a) => {
    let productCardsRemain = false;

    await this.runSuggestedSearch($a, ($productCards) => {
      const $div = $('<div></div>');

      $div.append($productCards);

      this.removeExcludedAndUnavailableCards($div);

      productCardsRemain = $div.children().length > 0;
    });

    return productCardsRemain;
  }

  /**
   * Handles a suggested search's mouseenter event. If it is based
   * on a collection or product (and is not the currently performed
   * search), the search is performed, and any resulting product
   * cards are displayed in its callback.
   *
   * This event handler is also called from handleCartUpdatedEvent,
   * using the lastSuggestedSearchEvent, in order to update the
   * displayed product cards with their "latest" states (reflecting
   * purchase limits, etc.).
   *
   * @param {MouseEvent} e The search suggestion's mouseenter event.
   *
   * @returns {Promise}
   */
  handleSuggestedSearchMouseenter = async (e) => {
    const $a = $(e.currentTarget);

    if (!$a.is('[data-collection], [data-product]')) return {};

    let isLastEvent = false;

    if (this.lastSuggestedSearchEvent !== e) {
      this.lastSuggestedSearchEvent = e;
    } else {
      isLastEvent = true;
    }

    if (!isLastEvent && $a.is('[aria-current="true"]')) return {};

    return new Promise((resolve) => {
      this.suggestedSearches.$ul.find('[aria-current="true"]').attr('aria-current', false);

      $a.attr('aria-current', true);

      this.runSuggestedSearch($a, ($productCards) => {
        this.popularSuggestions.$row.empty().append($productCards);

        this.removeExcludedAndUnavailableCards(this.popularSuggestions.$row);

        if (this.popularSuggestions.$row.children().length) {
          this.popularSuggestions.$.show();
          this.popularProducts.$.hide();
          this.noResults.$.hide();
        } else {
          this.displayNoResults($a.text());
        }

        resolve();
      });
    })
  }

  /**
   * Handles a suggested search's click event. Publishes the 'search_results_click'
   * analytics event before proceeding to redirect the user to the intended URL.
   *
   * @param {MouseEvent} e The search suggestion's click event.
   */
  handleSuggestedSearchClick = (e) => {
    e.preventDefault();

    const $a = $(e.currentTarget);

    Shopify.analytics.publish('search_results_click', {
      search_term: this.form.$query.val(),
      result_type: 'suggested',
      suggested_term: $a.text(),
      suggestion_category: $a.parent().parent().siblings('.suggested-searches__subheading').text(),
      suggestion_type: 'text',
    });

    window.location.href = $a.attr('href');
  }

  /**
   * Handles a product card's link click event. Publishes the 'search_results_click'
   * analytics event before proceeding to redirect the user to the intended URL.
   *
   * @param {MouseEvent} e The product card's link click event.
   */
  handleProductCardLinkClick = (e) => {
    e.preventDefault();

    const $a = $(e.currentTarget);
    const $currentSuggestion = this.suggestedSearches.$.find('a[aria-current="true"]');

    Shopify.analytics.publish('search_results_click', {
      search_term: this.form.$query.val(),
      result_type: 'organic',
      suggested_term: $currentSuggestion.text(),
      suggestion_category: $currentSuggestion.parent().parent().siblings('.suggested-searches__subheading').text(),
      suggestion_type: 'product card',
    });

    window.location.href = $a.attr('href');
  }

  /**
   * Handles the 'CartUpdater.updating' custom event, disabling
   * the Popular Products and Popular Suggestions while updates
   * to the UI are performed.
   */
  handleCartUpdatingEvent = () => {
    this.popularProducts.$.addClass('popular-products--disabled');
    this.popularSuggestions.$.addClass('popular-suggestions--disabled');
  }

  /**
   * Handles the 'CartUpdater.updated' custom event, manually firing
   * the handleSuggestedSearchMouseenter event handler based on the
   * lastSuggestedSearchEvent, before enabling the Popular Products
   * and Popular Suggestions and displaying a toast after updates
   * to the UI are performed.
   */
  handleCartUpdatedEvent = async () => {
    if (this.lastSuggestedSearchEvent) {
      await this.handleSuggestedSearchMouseenter(this.lastSuggestedSearchEvent);
    }

    this.popularProducts.$.removeClass('popular-products--disabled');
    this.popularSuggestions.$.removeClass('popular-suggestions--disabled');

    toasts.show('added-to-cart');
  }

  /**
   * Displays "no results" content following a request with no resulting
   * predictive searches, collections, products, etc. The user's manually
   * entered or suggested search query is shown to them for context.
   *
   * @param {string} query The user's query.
   */
  displayNoResults = (query) => {
    this.noResults.$.show().find('.no-results__query').text(query);
    this.popularProducts.$.hide();
    this.popularSuggestions.$.hide();
  }

  /**
   * Resets the drawer to its default state.
   */
  resetDrawer = () => {
    this.topSuggestions.$.show();
    this.suggestedSearches.$.hide();

    this.popularProducts.$.show();
    this.popularSuggestions.$.hide();
    this.noResults.$.hide();
  }
}

(($) => {
  $(() => {
    new SearchDrawer();
  });
})(jQuery);